import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, ArrowLeft, BookOpen, MessageCircle, Building2, AlertTriangle, Search } from 'lucide-react';

const NotFound = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const quickLinks = [
    {
      name: 'Overview',
      href: '/',
      icon: Home,
      description: 'Dashboard overview and stats',
      color: 'blue'
    },
    {
      name: 'Knowledge Vault',
      href: '/knowledge',
      icon: BookOpen,
      description: 'Upload and manage your content',
      color: 'indigo'
    },
    {
      name: 'Widget Manager',
      href: '/widgets',
      icon: MessageCircle,
      description: 'Create and customize widgets',
      color: 'purple'
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header - matching dashboard style */}
      <header className="sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Branding */}
            <div className="flex items-center space-x-3">
              <img
                src="/logo.svg"
                alt="Uttik"
                className="h-10 w-10 object-contain rounded"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Uttik</h1>
                <p className="text-xs text-gray-500 font-medium">AI Answer Engine</p>
              </div>
            </div>

            {/* Back button */}
            <button
              onClick={() => navigate(-1)}
              className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:text-gray-900 bg-white hover:bg-gray-50 rounded-lg transition-colors border border-gray-300 shadow-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="font-medium">Go Back</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-4xl w-full">
          {/* 404 Section */}
          <div className={`text-center mb-12 transition-all duration-700 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            {/* Icon */}
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gray-100 rounded-full mb-6">
              <AlertTriangle className="w-12 h-12 text-black" />
            </div>

            {/* 404 Text */}
            <h1 className="text-6xl sm:text-7xl font-bold text-gray-900 mb-4">
              404
            </h1>
            <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800 mb-4">
              Page Not Found
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
              The page you're looking for doesn't exist or has been moved to a different location.
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center justify-center px-6 py-3 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all shadow-sm hover:shadow-md"
              >
                <Home className="w-5 h-5 mr-2" />
                Back to Dashboard
              </button>
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center justify-center px-6 py-3 bg-white text-gray-700 font-semibold border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all shadow-sm"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Go Back
              </button>
            </div>
          </div>

          {/* Quick Links */}
          {/* <div className={`transition-all duration-700 delay-200 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <div className="text-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Quick Navigation</h3>
              <p className="text-gray-600">Here are some popular sections you might be looking for</p>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-12">
              {quickLinks.map((link, index) => {
                const Icon = link.icon;
                const colorClasses = {
                  blue: 'bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-700',
                  indigo: 'bg-indigo-50 hover:bg-indigo-100 border-indigo-200 text-indigo-700',
                  purple: 'bg-purple-50 hover:bg-purple-100 border-purple-200 text-purple-700',
                };
                const iconColorClasses = {
                  blue: 'text-blue-600',
                  indigo: 'text-indigo-600',
                  purple: 'text-purple-600',
                };

                return (
                  <button
                    key={link.href}
                    onClick={() => navigate(link.href)}
                    className={`group relative bg-white rounded-lg p-6 border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all duration-200 text-left`}
                  >
                    <div className={`w-12 h-12 ${colorClasses[link.color]} rounded-lg flex items-center justify-center mb-4 transition-colors border`}>
                      <Icon className={`w-6 h-6 ${iconColorClasses[link.color]}`} />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                      {link.name}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {link.description}
                    </p>
                  </button>
                );
              })}
            </div>
          </div> */}

          {/* Help Section */}
          {/* <div className={`transition-all duration-700 delay-400 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-8 border border-blue-100 text-center">
              <Search className="w-10 h-10 text-blue-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Still need help?</h3>
              <p className="text-gray-600 mb-4">Contact our support team for assistance</p>
              <button onClick={() => window.open('https://www.uttik.com', '_blank')} className="px-6 py-2.5 bg-white text-blue-600 font-medium rounded-lg hover:bg-gray-50 transition-colors border border-blue-200 shadow-sm">
                Get Support
              </button>
            </div>
          </div> */}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-sm text-gray-600">
            © 2025 Uttik. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default NotFound;